import { useState } from 'react';
import { Linkified } from '@/components/linkified';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { HackerLlmEvaluation } from '@/types';

const DIMENSIONS = [
  { key: 'technicalCraft', label: 'Technical Craft' },
  { key: 'agency', label: 'Agency' },
  { key: 'shippingVelocity', label: 'Shipping Velocity' },
  { key: 'originality', label: 'Originality' },
  { key: 'trajectory', label: 'Trajectory' },
] as const;

type DimensionKey = (typeof DIMENSIONS)[number]['key'];

function getDimensionStructured(
  evaluation: HackerLlmEvaluation,
  key: DimensionKey,
): { score: number | null; pct: number | null } {
  switch (key) {
    case 'technicalCraft':
      return {
        score: evaluation.scoreTechnicalCraft,
        pct: evaluation.pctTechnicalCraft,
      };
    case 'agency':
      return { score: evaluation.scoreAgency, pct: evaluation.pctAgency };
    case 'shippingVelocity':
      return {
        score: evaluation.scoreShippingVelocity,
        pct: evaluation.pctShippingVelocity,
      };
    case 'originality':
      return {
        score: evaluation.scoreOriginality,
        pct: evaluation.pctOriginality,
      };
    case 'trajectory':
      return {
        score: evaluation.scoreTrajectory,
        pct: evaluation.pctTrajectory,
      };
  }
}

interface MemberEvaluation {
  hackerId: string;
  fullName: string;
  github: string | null;
  linkedin: string | null;
  evaluation: HackerLlmEvaluation | null;
}

const formatPercentile = (pct: number | null | undefined) =>
  pct == null ? null : `P${Math.round(pct * 100)}`;

const percentileColor = (pct: number | null | undefined) => {
  if (pct == null) return 'border-muted bg-muted text-muted-foreground';
  if (pct >= 0.9) return 'border-emerald-500 bg-emerald-100 text-emerald-900';
  if (pct >= 0.75) return 'border-emerald-300 bg-emerald-50 text-emerald-800';
  if (pct >= 0.5) return 'border-amber-300 bg-amber-50 text-amber-800';
  if (pct >= 0.25) return 'border-orange-300 bg-orange-50 text-orange-800';
  return 'border-red-300 bg-red-50 text-red-800';
};

function getDimension(
  evaluation: HackerLlmEvaluation,
  dim: (typeof DIMENSIONS)[number],
) {
  return getDimensionStructured(evaluation, dim.key);
}

function getRawDimension(
  raw: unknown,
  key: DimensionKey,
): {
  rationale?: string;
  confidence?: number;
  citations?: Array<{ source?: string; ref?: string; url?: string | null }>;
} | null {
  try {
    const r = raw as { result?: { scores?: Record<string, unknown> } };
    const entry = r?.result?.scores?.[key];
    if (entry && typeof entry === 'object') {
      return entry as ReturnType<typeof getRawDimension>;
    }
  } catch {
    // ignore
  }
  return null;
}

type RawShape = {
  result?: {
    insights?: Array<{ kind?: string; severity?: string; note?: string }>;
    concerns?: Array<{ kind?: string; severity?: string; note?: string }>;
    coverage?: Record<string, { status?: string; note?: string }>;
    evidence?: {
      github?: Record<string, unknown> | null;
      linkedin?: {
        currentRole?: string | null;
        notableRoles?: Array<{
          title?: string;
          organization?: string;
          startDate?: string;
          endDate?: string | null;
          current?: boolean;
          durationMonths?: number;
        }>;
        location?: string | null;
        followers?: number | null;
      } | null;
      twitter?: {
        handle?: string | null;
        followers?: number | null;
        bio?: string | null;
      } | null;
    };
    report?: {
      highlights?: string[];
      concerns?: string[];
      recommendedNextStep?: string;
      summaryBullets?: string[];
    };
    meta?: { rubricVersion?: string; generatedAt?: string };
  };
};

const severityColor = (severity: string | undefined) => {
  switch (severity) {
    case 'critical':
      return 'border-red-300 bg-red-50 text-red-800';
    case 'warning':
      return 'border-amber-300 bg-amber-50 text-amber-800';
    default:
      return 'border-blue-300 bg-blue-50 text-blue-800';
  }
};

export function HackerEvaluation({ member }: { member: MemberEvaluation }) {
  const [openDimension, setOpenDimension] = useState<DimensionKey | null>(null);
  const [openAiReview, setOpenAiReview] = useState(false);
  const [openFullReport, setOpenFullReport] = useState(false);
  const evaluation = member.evaluation;

  if (!evaluation || evaluation.pctComposite == null) {
    return null;
  }

  const raw = (evaluation.raw ?? {}) as RawShape;
  const composite = formatPercentile(evaluation.pctComposite);

  const activeDimension = openDimension
    ? (DIMENSIONS.find((d) => d.key === openDimension) ?? null)
    : null;
  const activeRaw = activeDimension
    ? getRawDimension(raw, activeDimension.key)
    : null;
  const activeStructured = activeDimension
    ? getDimension(evaluation, activeDimension)
    : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
          AI Evaluation
        </span>
        <div className="flex items-center gap-2">
          {evaluation.finalMetric != null && (
            <span className="font-mono text-muted-foreground text-xs">
              metric {evaluation.finalMetric.toFixed(2)}
            </span>
          )}
          <Badge
            variant="outline"
            className={`font-mono ${percentileColor(evaluation.pctComposite)}`}
          >
            AI rank {composite ?? 'N/A'}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {DIMENSIONS.map((dim) => {
          const { score, pct } = getDimension(evaluation, dim);
          const dimRaw = getRawDimension(raw, dim.key);
          return (
            <button
              key={dim.key}
              type="button"
              onClick={() => setOpenDimension(dim.key)}
              className="flex flex-col gap-1 rounded-md border p-2 text-left transition-colors hover:bg-muted"
            >
              <span className="text-muted-foreground text-xs">{dim.label}</span>
              <div className="flex items-baseline gap-2">
                <span className="font-mono font-semibold text-lg">
                  {score ?? '—'}
                </span>
                <span
                  className={`rounded px-1 font-mono text-[10px] ${percentileColor(pct)}`}
                >
                  {formatPercentile(pct) ?? '—'}
                </span>
              </div>
              <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-foreground/60"
                  style={{
                    width: `${Math.round((pct ?? 0) * 100)}%`,
                  }}
                />
              </div>
              {typeof dimRaw?.confidence === 'number' && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">
                    conf
                  </span>
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-blue-500/60"
                      style={{
                        width: `${Math.round(dimRaw.confidence * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenAiReview(true)}
        >
          AI Review
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenFullReport(true)}
        >
          Full AI Report
        </Button>
      </div>

      <Dialog
        open={openDimension !== null}
        onOpenChange={(o) => {
          if (!o) setOpenDimension(null);
        }}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          {activeDimension && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {activeDimension.label} — {member.fullName}
                </DialogTitle>
                <DialogDescription>
                  Score {activeStructured?.score ?? '—'} ·{' '}
                  {formatPercentile(activeStructured?.pct) ?? 'no percentile'}
                  {typeof activeRaw?.confidence === 'number' && (
                    <>
                      {' '}
                      · confidence {(activeRaw.confidence * 100).toFixed(0)}%
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {activeRaw?.rationale ? (
                  <p className="leading-relaxed">{activeRaw.rationale}</p>
                ) : (
                  <p className="text-muted-foreground">
                    No rationale provided.
                  </p>
                )}
                {Array.isArray(activeRaw?.citations) &&
                  activeRaw.citations.length > 0 && (
                    <div>
                      <p className="font-medium text-xs uppercase tracking-wide">
                        Citations
                      </p>
                      <ul className="mt-1 space-y-1">
                        {activeRaw.citations.map((c, i) => (
                          <li
                            key={`${c.source ?? 'src'}-${c.url ?? c.ref ?? i}`}
                            className="text-xs"
                          >
                            <span className="text-muted-foreground">
                              [{c.source ?? 'source'}]
                            </span>{' '}
                            {c.url ? (
                              <a
                                href={c.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                {c.ref ?? c.url}
                              </a>
                            ) : (
                              <span>{c.ref}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openAiReview} onOpenChange={setOpenAiReview}>
        <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Review — {member.fullName}</DialogTitle>
            <DialogDescription>
              Highlights and concerns surfaced by the agent.
            </DialogDescription>
          </DialogHeader>
          <AiReviewBody raw={raw} />
        </DialogContent>
      </Dialog>

      <Dialog open={openFullReport} onOpenChange={setOpenFullReport}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Full AI Report — {member.fullName}</DialogTitle>
            <DialogDescription>
              Complete agent output. Sections render only when data is
              available.
            </DialogDescription>
          </DialogHeader>
          <FullReportBody raw={raw} evaluation={evaluation} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AiReviewBody({ raw }: { raw: RawShape }) {
  const reportHighlights = raw.result?.report?.highlights ?? [];
  const reportConcerns = raw.result?.report?.concerns ?? [];
  const insights = raw.result?.insights ?? [];
  const concerns = raw.result?.concerns ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <section>
        <h4 className="mb-2 font-medium text-sm">Highlights</h4>
        {reportHighlights.length === 0 && insights.length === 0 ? (
          <p className="text-muted-foreground text-xs">No highlights.</p>
        ) : (
          <ul className="space-y-2">
            {reportHighlights.map((h) => (
              <li
                key={`hi-${h}`}
                className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-emerald-900 text-xs"
              >
                {h}
              </li>
            ))}
            {insights.map((ins) => (
              <li
                key={`ins-${ins.kind ?? ''}-${ins.note ?? ''}`}
                className={`rounded-md border p-2 text-xs ${severityColor(ins.severity)}`}
              >
                <span className="font-medium">{ins.kind ?? 'insight'}: </span>
                {ins.note}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h4 className="mb-2 font-medium text-sm">Concerns</h4>
        {reportConcerns.length === 0 && concerns.length === 0 ? (
          <p className="text-muted-foreground text-xs">No concerns.</p>
        ) : (
          <ul className="space-y-2">
            {reportConcerns.map((c) => (
              <li
                key={`rc-${c}`}
                className="rounded-md border border-red-200 bg-red-50 p-2 text-red-900 text-xs"
              >
                {c}
              </li>
            ))}
            {concerns.map((c) => (
              <li
                key={`sc-${c.kind ?? ''}-${c.note ?? ''}`}
                className={`rounded-md border p-2 text-xs ${severityColor(c.severity)}`}
              >
                <span className="font-medium">{c.kind ?? 'concern'}: </span>
                {c.note}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function safeParse<T>(parser: () => T): T | null {
  try {
    return parser();
  } catch {
    return null;
  }
}

function FullReportBody({
  raw,
  evaluation,
}: {
  raw: RawShape;
  evaluation: HackerLlmEvaluation;
}) {
  const sections: Array<{ key: string; title: string; node: React.ReactNode }> =
    [];

  sections.push({
    key: 'scores',
    title: 'Scores',
    node: (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {DIMENSIONS.map((dim) => {
          const { score, pct } = getDimension(evaluation, dim);
          const dimRaw = getRawDimension(raw, dim.key);
          return (
            <div key={dim.key} className="rounded-md border p-2">
              <div className="text-muted-foreground text-xs">{dim.label}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono font-semibold">{score ?? '—'}</span>
                <span
                  className={`rounded px-1 font-mono text-[10px] ${percentileColor(pct)}`}
                >
                  {formatPercentile(pct) ?? '—'}
                </span>
              </div>
              {typeof dimRaw?.confidence === 'number' && (
                <div className="text-[10px] text-muted-foreground">
                  conf {(dimRaw.confidence * 100).toFixed(0)}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    ),
  });

  const review = safeParse(() => raw.result?.report);
  if (review?.highlights?.length || review?.concerns?.length) {
    sections.push({
      key: 'review',
      title: 'Highlights & Concerns',
      node: <AiReviewBody raw={raw} />,
    });
  }

  const insights = safeParse(() => raw.result?.insights ?? []);
  if (insights && insights.length > 0) {
    sections.push({
      key: 'insights',
      title: 'Insights',
      node: (
        <ul className="space-y-1">
          {insights.map((entry) => (
            <li
              key={`${entry.kind ?? ''}-${entry.note ?? ''}`}
              className={`rounded-md border p-2 text-xs ${severityColor(entry.severity)}`}
            >
              <span className="font-medium">{entry.kind}: </span>
              {entry.note}
            </li>
          ))}
        </ul>
      ),
    });
  }

  const github = safeParse(
    () => raw.result?.evidence?.github as Record<string, unknown> | undefined,
  );
  if (github) {
    sections.push({
      key: 'github',
      title: 'Evidence — GitHub',
      node: <EvidenceGitHub data={github} />,
    });
  }

  const linkedin = safeParse(() => raw.result?.evidence?.linkedin);
  if (
    linkedin &&
    (linkedin.currentRole ||
      (linkedin.notableRoles && linkedin.notableRoles.length > 0) ||
      linkedin.location)
  ) {
    sections.push({
      key: 'linkedin',
      title: 'Evidence — LinkedIn',
      node: <EvidenceLinkedIn data={linkedin} />,
    });
  }

  const twitter = safeParse(() => raw.result?.evidence?.twitter);
  if (twitter?.handle) {
    sections.push({
      key: 'twitter',
      title: 'Evidence — Twitter',
      node: <EvidenceTwitter data={twitter} />,
    });
  }

  if (review?.summaryBullets && review.summaryBullets.length > 0) {
    sections.push({
      key: 'summary-bullets',
      title: 'Summary',
      node: (
        <ul className="list-disc pl-5 text-sm">
          {review.summaryBullets.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      ),
    });
  }

  const meta = raw.result?.meta;
  const coverage = raw.result?.coverage;
  sections.push({
    key: 'meta',
    title: 'Coverage & Meta',
    node: (
      <div className="space-y-2 text-muted-foreground text-xs">
        {coverage && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(coverage).map(([source, info]) => (
              <Badge key={source} variant="outline">
                {source}: {info?.status ?? 'unknown'}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {meta?.rubricVersion && <span>rubric {meta.rubricVersion}</span>}
          {evaluation.evaluatedAt && (
            <span>
              evaluated {new Date(evaluation.evaluatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    ),
  });

  const handledKeys = new Set([
    'scores',
    'insights',
    'concerns',
    'evidence',
    'report',
    'meta',
    'coverage',
    'applicant',
  ]);
  const unknownEntries = Object.entries(raw.result ?? {}).filter(
    ([k]) => !handledKeys.has(k),
  );

  return (
    <div className="space-y-5">
      {sections.map((s) => (
        <section key={s.key}>
          <h4 className="mb-2 font-semibold text-sm uppercase tracking-wide">
            {s.title}
          </h4>
          {s.node}
        </section>
      ))}
      {unknownEntries.length > 0 && (
        <section>
          <h4 className="mb-2 font-semibold text-sm uppercase tracking-wide">
            Unknown Fields
          </h4>
          <pre className="overflow-x-auto rounded-md bg-muted p-2 text-xs">
            {JSON.stringify(Object.fromEntries(unknownEntries), null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}

function EvidenceGitHub({ data }: { data: Record<string, unknown> }) {
  const followers = (data.followers as number | undefined) ?? null;
  const following = (data.following as number | undefined) ?? null;
  const publicRepos = (data.publicRepos as number | undefined) ?? null;
  const accountCreatedAt = data.accountCreatedAt as string | undefined;
  const contributions = data.contributions as
    | {
        lastYearTotal?: number;
        public?: number;
        private?: number;
        activeDaysOutOf370?: number;
      }
    | undefined;
  const topRepos = data.topRepos as
    | Array<{
        name?: string;
        url?: string;
        language?: string;
        stars?: number;
        pushedAt?: string;
      }>
    | undefined;
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-3 text-muted-foreground">
        {followers != null && <span>followers {followers}</span>}
        {following != null && <span>following {following}</span>}
        {publicRepos != null && <span>public repos {publicRepos}</span>}
        {accountCreatedAt && (
          <span>since {new Date(accountCreatedAt).getFullYear()}</span>
        )}
      </div>
      {contributions && (
        <div className="rounded-md border p-2">
          <div className="font-medium">Last year contributions</div>
          <div className="flex flex-wrap gap-3 text-muted-foreground">
            <span>total {contributions.lastYearTotal ?? '—'}</span>
            <span>public {contributions.public ?? '—'}</span>
            <span>private {contributions.private ?? '—'}</span>
            <span>
              active days {contributions.activeDaysOutOf370 ?? '—'}/370
            </span>
          </div>
        </div>
      )}
      {topRepos && topRepos.length > 0 && (
        <div>
          <div className="font-medium">Top repos</div>
          <ul className="mt-1 space-y-1">
            {topRepos.slice(0, 8).map((r) => (
              <li
                key={r.url ?? r.name ?? ''}
                className="flex flex-wrap items-baseline gap-2"
              >
                {r.url ? (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono underline"
                  >
                    {r.name}
                  </a>
                ) : (
                  <span className="font-mono">{r.name}</span>
                )}
                {r.language && (
                  <span className="text-muted-foreground">{r.language}</span>
                )}
                {typeof r.stars === 'number' && (
                  <span className="text-muted-foreground">★ {r.stars}</span>
                )}
                {r.pushedAt && (
                  <span className="text-muted-foreground">
                    {new Date(r.pushedAt).toLocaleDateString()}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EvidenceLinkedIn({
  data,
}: {
  data: NonNullable<NonNullable<RawShape['result']>['evidence']>['linkedin'];
}) {
  if (!data) return null;
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap gap-3 text-muted-foreground">
        {data.currentRole && <span>{data.currentRole}</span>}
        {data.location && <span>· {data.location}</span>}
      </div>
      {data.notableRoles && data.notableRoles.length > 0 && (
        <ul className="space-y-1">
          {data.notableRoles.map((r) => (
            <li
              key={`${r.title ?? ''}-${r.organization ?? ''}-${r.startDate ?? ''}`}
              className="rounded-md border p-2"
            >
              <div className="font-medium">{r.title}</div>
              <div className="text-muted-foreground">
                {r.organization}
                {(r.startDate || r.endDate) && (
                  <>
                    {' · '}
                    {r.startDate
                      ? new Date(r.startDate).toLocaleDateString()
                      : '?'}
                    {' → '}
                    {r.current
                      ? 'present'
                      : r.endDate
                        ? new Date(r.endDate).toLocaleDateString()
                        : '?'}
                  </>
                )}
                {typeof r.durationMonths === 'number' &&
                  ` (${r.durationMonths} mo)`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EvidenceTwitter({
  data,
}: {
  data: NonNullable<NonNullable<RawShape['result']>['evidence']>['twitter'];
}) {
  if (!data?.handle) return null;
  return (
    <div className="space-y-1 text-xs">
      <div className="flex flex-wrap gap-3">
        <a
          href={`https://twitter.com/${data.handle}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono underline"
        >
          @{data.handle}
        </a>
        {typeof data.followers === 'number' && (
          <span className="text-muted-foreground">
            {data.followers} followers
          </span>
        )}
      </div>
      {data.bio && (
        <p className="text-muted-foreground">
          <Linkified text={data.bio} />
        </p>
      )}
    </div>
  );
}
